import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Smartphone, Check, Loader2, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface NativeAppWaitlistModalProps {
  open: boolean;
  onClose: () => void;
}

const NativeAppWaitlistModal = ({ open, onClose }: NativeAppWaitlistModalProps) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [platform, setPlatform] = useState<"ios" | "android" | "both">("ios");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [removeMode, setRemoveMode] = useState(false);
  const [removed, setRemoved] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      const { error } = await (supabase.from("app_waitlist") as any).insert({
        email: email.trim().toLowerCase(),
        platform,
      });

      if (error) {
        if (error.code === "23505") {
          await (supabase.from("app_waitlist") as any)
            .update({ platform })
            .eq("email", email.trim().toLowerCase());
          setSuccess(true);
        } else {
          throw error;
        }
      } else {
        setSuccess(true);
      }
    } catch (err: any) {
      toast.error(err.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim()) return;

    setLoading(true);
    try {
      const { error, count } = await (supabase.from("app_waitlist") as any)
        .delete({ count: "exact" })
        .eq("email", email.trim().toLowerCase());

      if (error) throw error;

      if (count === 0) {
        toast.error(t("waitlist.notFound", "That email wasn't found on the waitlist."));
      } else {
        setRemoved(true);
      }
    } catch (err: any) {
      toast.error(err.message || t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setSuccess(false);
    setRemoved(false);
    setRemoveMode(false);
    setEmail("");
    setPlatform("ios");
    onClose();
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <AnimatePresence>
        <motion.div
          initial={{ scale: 0.95, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.95, opacity: 0 }}
          className="relative w-full max-w-sm bg-card border border-border rounded-2xl p-5 shadow-xl space-y-4"
        >
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 p-1 rounded-lg hover:bg-secondary transition-colors"
          >
            <X className="w-4 h-4 text-muted-foreground" />
          </button>

          <div className="text-center space-y-2 pr-6">
            <div className="w-12 h-12 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto">
              {removeMode ? (
                <Trash2 className="w-6 h-6 text-primary" />
              ) : (
                <Smartphone className="w-6 h-6 text-primary" />
              )}
            </div>
            <h2 className="font-display text-lg font-bold text-foreground">
              {removeMode
                ? t("waitlist.removeTitle", "Leave the Waitlist")
                : t("waitlist.title", "Native App Waitlist")}
            </h2>
            <p className="text-xs text-muted-foreground leading-relaxed">
              {removeMode
                ? t("waitlist.removeDescription", "Enter the email you signed up with and we'll remove you from the waitlist.")
                : t("waitlist.description", "We're hoping to bring Ayeris to the App Store & Google Play. Register your interest below — the more people who sign up, the more likely it'll happen (and sooner!)")}
            </p>
          </div>

          {/* Success state (joined) */}
          {success ? (
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center space-y-3 py-4"
            >
              <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center mx-auto">
                <Check className="w-7 h-7 text-primary" />
              </div>
              <p className="text-sm font-semibold text-foreground">
                {t("waitlist.success", "You're on the list! 🎉")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("waitlist.successDesc", "We'll send you an email when the app is available on your chosen platform.")}
              </p>
              <button
                onClick={handleClose}
                className="mt-2 px-6 py-2.5 rounded-xl bg-secondary text-foreground text-sm font-medium active:scale-95 transition-transform"
              >
                {t("common.close")}
              </button>
            </motion.div>
          ) : removed ? (
            /* Success state (removed) */
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              className="text-center space-y-3 py-4"
            >
              <div className="w-14 h-14 rounded-full bg-primary/15 flex items-center justify-center mx-auto">
                <Check className="w-7 h-7 text-primary" />
              </div>
              <p className="text-sm font-semibold text-foreground">
                {t("waitlist.removed", "You've been removed ✓")}
              </p>
              <p className="text-xs text-muted-foreground">
                {t("waitlist.removedDesc", "Your email has been deleted from the waitlist. You won't receive any emails from us about the app launch.")}
              </p>
              <button
                onClick={handleClose}
                className="mt-2 px-6 py-2.5 rounded-xl bg-secondary text-foreground text-sm font-medium active:scale-95 transition-transform"
              >
                {t("common.close")}
              </button>
            </motion.div>
          ) : removeMode ? (
            /* Remove form */
            <form onSubmit={handleRemove} className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">
                  {t("common.email")}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full py-3 rounded-xl bg-destructive text-destructive-foreground font-display font-semibold text-sm active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t("waitlist.removeSubmit", "Remove Me")
                )}
              </button>

              <button
                type="button"
                onClick={() => { setRemoveMode(false); setEmail(""); }}
                className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors"
              >
                ← {t("waitlist.backToJoin", "Back to waitlist sign-up")}
              </button>
            </form>
          ) : (
            /* Join form */
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-2 block">
                  {t("waitlist.platformLabel", "Which platform?")}
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {(["ios", "android", "both"] as const).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setPlatform(p)}
                      className={`py-2.5 rounded-xl text-xs font-semibold transition-colors ${
                        platform === p
                          ? "bg-primary text-primary-foreground"
                          : "bg-secondary text-muted-foreground border border-border hover:text-foreground"
                      }`}
                    >
                      {p === "ios"
                        ? "🍎 iOS"
                        : p === "android"
                        ? "🤖 Android"
                        : t("waitlist.both", "Both")}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs text-muted-foreground uppercase tracking-wider mb-1 block">
                  {t("common.email")}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  placeholder="you@example.com"
                  className="w-full px-4 py-2.5 rounded-xl bg-secondary border border-border text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
                />
              </div>

              <button
                type="submit"
                disabled={loading || !email.trim()}
                className="w-full py-3 rounded-xl bg-gradient-iris text-primary-foreground font-display font-semibold text-sm shadow-iris active:scale-95 transition-transform disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  t("waitlist.submit", "Join the Waitlist")
                )}
              </button>

              <p className="text-[10px] text-muted-foreground/60 text-center">
                {t("waitlist.privacy", "We'll only use your email to notify you about the app launch.")}
              </p>

              <button
                type="button"
                onClick={() => { setRemoveMode(true); setEmail(""); }}
                className="w-full text-[11px] text-muted-foreground/60 hover:text-muted-foreground transition-colors text-center"
              >
                {t("waitlist.alreadySignedUp", "Already signed up? Remove yourself")}
              </button>
            </form>
          )}
        </motion.div>
      </AnimatePresence>
    </div>
  );
};

export default NativeAppWaitlistModal;
